import { useEffect, useState } from "react";
import CrudPage from "@/components/CrudPage";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function maskToRegex(value) {
  const raw = String(value || "").trim();
  if (!raw) return raw;
  if (raw.startsWith("^") || raw.endsWith("$") || raw.includes("[")) return raw;
  const escaped = raw.replace(/[.+?^${}()|[\]\\]/g, "\\$&");
  let out = "";
  for (const ch of escaped) {
    if (ch === "x" || ch === "X") out += "[0-9]";
    else if (ch === "*") out += ".*";
    else out += ch;
  }
  return `^${out}$`.replace(/(?:\[0-9\]){2,}/g, (match) => `[0-9]{${match.length / 5}}`);
}

function regexToMask(value) {
  let raw = String(value || "");
  if (raw === "^0(.+)$") return "0*";
  if (raw === "^0(1[0-9]{2})$") return "01xx";
  const simple = raw.match(/^\^([0-9]+)(?:\[0-9\]|\[0-9\]\{(\d+)\})\$$/);
  if (simple) return simple[1] + "x".repeat(simple[2] ? Number(simple[2]) : 1);
  raw = raw.replace(/^\^/, "").replace(/\$$/, "");
  raw = raw.replace(/\[0-9\]\{(\d+)\}/g, (_, count) => "x".repeat(Number(count)));
  return raw.replace(/\[0-9\]/g, "x").replace(/\.\*/g, "*");
}

function formatCidDisplay(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (digits.startsWith("55") && digits.length >= 12) {
    return digits.slice(2);
  }
  return digits || raw;
}

function PatternPreview({ value }) {
  const mask = regexToMask(value);
  return (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-sm">{mask}</span>
      {mask !== value && <span className="font-mono text-[10px] text-[color:var(--text-muted)]">{value}</span>}
    </div>
  );
}

export default function Rotas() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const [ops, setOps] = useState([]);
  const [ipbxs, setIpbxs] = useState([]);
  const [anuncios, setAnuncios] = useState([]);
  const [dids, setDids] = useState([]);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    (async () => {
      const [operators, pbxs, announcements, didRows, tenantRows] = await Promise.all([
        api.get("/operadoras"),
        api.get("/ipbxs"),
        api.get("/anuncios"),
        api.get("/dids"),
        isSuperAdmin ? api.get("/tenants") : Promise.resolve({ data: [] }),
      ]);
      setOps(operators.data);
      setIpbxs(pbxs.data);
      setAnuncios(announcements.data);
      setDids(didRows.data);
      setTenants(tenantRows.data);
    })();
  }, [isSuperAdmin]);

  const opMap = Object.fromEntries(ops.map((row) => [row.id, row.nome]));
  const ipbxMap = Object.fromEntries(ipbxs.map((row) => [row.id, row.nome]));
  const anuncioMap = Object.fromEntries(anuncios.map((row) => [row.id, row.nome]));
  const tenantMap = Object.fromEntries(tenants.map((row) => [row.id, row.nome]));
  const belongsToTenant = (row, tenantId) => !tenantId || row.tenant_id === tenantId || row.universal === true;
  const optionsFor = (rows, form, emptyLabel) => [
    { value: "", label: emptyLabel },
    ...rows.filter((row) => belongsToTenant(row, form.tenant_id)).map((row) => ({ value: row.id, label: row.nome })),
  ];

  return (
    <>
      <details className="sbc-card mb-4 p-4">
        <summary className="cursor-pointer text-sm font-semibold">FAQ de regras de discagem e LCR</summary>
        <div className="mt-4 grid gap-3 text-xs text-[color:var(--text-secondary)] md:grid-cols-2">
          <div><strong className="text-[color:var(--text-primary)]">Todas as chamadas externas</strong><div className="mt-1 font-mono">^0(.+)$</div><p>Recebe o prefixo 0 e envia ao tronco somente os digitos capturados depois dele.</p></div>
          <div><strong className="text-[color:var(--text-primary)]">Somente DDD 71</strong><div className="mt-1 font-mono">^0(71[2-9][0-9]{7,8})$</div><p>Seleciona numeros fixos ou moveis do DDD 71.</p></div>
          <div><strong className="text-[color:var(--text-primary)]">Celulares do DDD 71</strong><div className="mt-1 font-mono">^0(719[0-9]{8})$</div><p>Permite criar custo e operadora exclusivos para celular.</p></div>
          <div><strong className="text-[color:var(--text-primary)]">Numeros locais</strong><div className="mt-1 font-mono">0 + numero local</div><p>Com o DDD do cliente cadastrado, o IPBX acrescenta o DDD antes de avaliar a rota.</p></div>
          <div><strong className="text-[color:var(--text-primary)]">Prioridade e alternativa automatica</strong><p className="mt-1">O menor numero de prioridade tenta primeiro. Com a alternativa automatica ativa, falhas seguem para a proxima operadora.</p></div>
          <div><strong className="text-[color:var(--text-primary)]">Custo e horario</strong><p className="mt-1">Entre rotas da mesma prioridade, o menor custo vem primeiro. Inicio, fim e dias limitam o uso da regra.</p></div>
        </div>
      </details>

      <CrudPage
        title="Rotas / LCR"
        subtitle="Least Cost Routing"
        endpoint="/rotas"
        testidPrefix="rotas"
        headers={[...(isSuperAdmin ? ["Cliente"] : []), "Nome", "Padrao", "Operadora", "IPBX", "Anuncio", "CID enviado", "Prioridade", "Custo", "Alternativa automatica", "Horario", "Status"]}
        renderRow={(row) => (
          <>
            {isSuperAdmin && <td className="font-medium">{tenantMap[row.tenant_id] || row.tenant_id || "-"}</td>}
            <td className="font-medium">{row.nome}</td>
            <td><PatternPreview value={row.padrao} /></td>
            <td>{opMap[row.operadora_id] || <span className="text-[color:var(--text-muted)]">-</span>}</td>
            <td>{ipbxMap[row.ipbx_id] || <span className="text-[color:var(--text-muted)]">-</span>}</td>
            <td>{anuncioMap[row.anuncio_id] || <span className="text-[color:var(--text-muted)]">-</span>}</td>
            <td className="font-mono text-xs">{row.did_saida ? formatCidDisplay(row.did_saida) : "padrao do ramal/cliente"}</td>
            <td className="font-mono">{row.prioridade}</td>
            <td className="font-mono">R$ {Number(row.custo).toFixed(2)}</td>
            <td>{row.fallback_on_fail !== false ? <span className="sbc-tag tag-online">ativo</span> : <span className="sbc-tag tag-offline">nao</span>}</td>
            <td className="font-mono text-xs">{row.horario_inicio || row.horario_fim ? `${row.horario_inicio || "00:00"}-${row.horario_fim || "23:59"}` : "24h"}</td>
            <td>{row.enabled ? <span className="sbc-tag tag-online">ativa</span> : <span className="sbc-tag tag-offline">inativa</span>}</td>
          </>
        )}
        defaultValues={{ tenant_id: user?.tenant_id || "", nome: "Saida geral - discar 0", padrao: "0*", operadora_id: "", ipbx_id: "", anuncio_id: "", did_saida: "", prioridade: 100, custo: 0, fallback_on_fail: true, horario_inicio: "", horario_fim: "", dias_semana: "1-7", enabled: true }}
        transformFormOpen={(form) => ({ ...form, padrao: regexToMask(form.padrao) })}
        transformSubmit={(form) => ({ ...form, padrao: maskToRegex(form.padrao) })}
        fields={[
          ...(isSuperAdmin ? [{ key: "tenant_id", label: "Cliente da rota", type: "select", full: true, options: [{ value: "", label: "-- selecione o cliente --" }, ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.nome }))], onChange: (next) => ({ ...next, operadora_id: "", ipbx_id: "", anuncio_id: "", did_saida: "" }), hint: "A rota ficara disponivel somente para o cliente selecionado." }] : []),
          { key: "nome", label: "Nome", full: true, placeholder: "Ex: Nacional Vivo" },
          { key: "padrao", label: "Destino", mono: true, full: true, placeholder: "0*", hint: "Padrao do Grupo Icore: saida geral usa 0* e servicos publicos usam 01xx. Regex avancado funciona se comecar com ^." },
          { key: "operadora_id", label: "Operadora", type: "select", options: (form) => optionsFor(ops, form, "-- selecione --") },
          { key: "ipbx_id", label: "IPBX destino", type: "select", options: (form) => optionsFor(ipbxs, form, "-- opcional --") },
          { key: "anuncio_id", label: "Anuncio antes do encaminhamento", type: "select", options: (form) => optionsFor(anuncios, form, "Sem anuncio") },
          { key: "did_saida", label: "CID enviado", type: "select", options: (form) => [{ value: "", label: "Usar padrao do ramal/cliente" }, ...dids.filter((did) => did.enabled !== false && belongsToTenant(did, form.tenant_id)).map((did) => ({ value: did.numero, label: `${formatCidDisplay(did.numero)} - ${opMap[did.operadora_id] || "sem operadora"}` }))], hint: "Cadastre os numeros no menu DIDs." },
          { key: "prioridade", label: "Prioridade (menor = maior)", type: "number", mono: true },
          { key: "custo", label: "Custo por minuto", type: "number", mono: true },
          { key: "fallback_on_fail", label: "Alternativa automatica", type: "toggle" },
          { key: "horario_inicio", label: "Horario inicio", placeholder: "08:00", mono: true, hint: "Opcional; use HH:MM." },
          { key: "horario_fim", label: "Horario fim", placeholder: "18:00", mono: true },
          { key: "dias_semana", label: "Dias da semana", placeholder: "1-5", mono: true, hint: "1=segunda, 7=domingo. Ex: 1-5 ou 1-7." },
          { key: "enabled", label: "Ativada", type: "toggle" },
        ]}
      />
    </>
  );
}
